import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Home } from "lucide-react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("error"); return; }
      } catch (err) {
        console.error("payment status poll failed (will retry):", err);
      }
      if (tries++ < 8) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-5 text-center">
      {status === "checking" && (<><Loader2 className="mb-4 animate-spin text-camel" size={40} /><p className="text-taupe">Confirming your reservation…</p></>)}
      {status === "paid" && (
        <div className="animate-fade-up">
          <CheckCircle2 className="mx-auto mb-4 text-camel" size={56} />
          <h1 className="text-4xl">You're booked!</h1>
          <p className="mt-3 text-taupe">A confirmation email is on its way, {info?.guest_name?.split(" ")[0] || "traveler"}.</p>
          <div className="mt-6 rounded-3xl card-lux p-6 text-left">
            <div className="text-lg font-medium text-ink">{info?.property_name}</div>
            <div className="mt-1 text-sm text-taupe">{info?.check_in} → {info?.check_out}</div>
            <div className="mt-3 flex justify-between border-t border-black/5 pt-3 font-semibold text-ink">
              <span>Total paid</span><span>{money(info?.amount)}</span>
            </div>
            {info?.amount > 0 && (
              <div data-testid="direct-savings-confirm" className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
                You saved ~{money(Math.round(info.amount / 0.85 - info.amount))} by booking direct instead of Airbnb!
              </div>
            )}
          </div>
          <Link to="/"><Button className="mt-6 rounded-full btn-ink px-6"><Home size={16} className="mr-2" /> Back home</Button></Link>
        </div>
      )}
      {(status === "pending" || status === "error") && (
        <div>
          <h1 className="text-3xl">{status === "pending" ? "Payment processing" : "Something went wrong"}</h1>
          <p className="mt-3 text-taupe">{status === "pending" ? "Your payment is still processing. Check your email shortly." : "We couldn't confirm your payment."}</p>
          <Link to="/rentals"><Button className="mt-6 rounded-full btn-ink px-6">Browse rentals</Button></Link>
        </div>
      )}
    </div>
  );
}
